import React, { useState, useEffect } from 'react';
import { formatCurrency, formatDate, formatDateHuman, getOfficeSettings } from '../utils/formatUtils';
import { useParams } from 'react-router-dom';
import {
  Clock, Phone, Mail, MapPin, CheckCircle, ChevronLeft, ChevronRight,
  Lock, Globe, Building2, Video, PhoneCall, AlertCircle, Loader2,
  User, FileText, CreditCard, Download, Plus, MessageCircle, Calendar
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';

const PublicBooking = () => {
  const { slug } = useParams();
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [lawyerTypes, setLawyerTypes] = useState([]);
  const [selectedLawyer, setSelectedLawyer] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [priceInfo, setPriceInfo] = useState(null);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [submitting, setSubmitting] = useState(false);
  const [booking, setBooking] = useState(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [formData, setFormData] = useState({
    name: '', phone: '', email: '', location_type: 'in_person', notes: '', intake_responses: {}
  });
  const [fieldErrors, setFieldErrors] = useState({ name: "", phone: "", email: "" });
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);
  const [verifiedPhone, setVerifiedPhone] = useState('');
  const [verifyChannel, setVerifyChannel] = useState('');
  const [verifiedContact, setVerifiedContact] = useState('');
  const [lawyerSearch, setLawyerSearch] = useState('');
  const [designationFilter, setDesignationFilter] = useState('all');

  // Validation functions
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    if (!phone) return 'Phone number is required';
    if (digitsOnly.length < 10) return 'Please enter a valid 10-digit mobile number';
    if (digitsOnly.length > 12) return 'Phone number is too long';
    return '';
  };

  const validateEmail = (email) => {
    if (!email) return ''; // Email is optional
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Please enter a valid email address';
    // Check for common typos
    const commonTypos = ['gamil.com', 'gmial.com', 'gmal.com', 'gnail.com', 'gmail.con', 'gmail.co'];
    const domain = email.split('@')[1]?.toLowerCase();
    if (commonTypos.includes(domain)) return `Did you mean gmail.com? Please check your email`;
    return '';
  };

  const validateName = (name) => {
    if (!name || name.trim().length < 2) return 'Please enter your full name';
    return '';
  };

  const handleFieldBlur = (field, value) => {
    let error = '';
    if (field === 'phone') error = validatePhone(value);
    if (field === 'email') error = validateEmail(value);
    if (field === 'name') error = validateName(value);
    setFieldErrors(prev => ({ ...prev, [field]: error }));
  };

  const themeColor = pageData?.theme_color || '#1E40AF';

  // Fetch page data on mount
  useEffect(() => {
    const fetchPageData = async () => {
      try {
        const response = await fetch('/api/appointments/book/' + slug);
        const data = await response.json();
        if (data.success) {
          setPageData(data.data);
          if (data.data.employee_id) {
            const lawyer = data.data.lawyers?.[0] || {};
            setSelectedLawyer({
              id: data.data.employee_id,
              first_name: lawyer.first_name || data.data.lawyer?.name?.split(' ')[0] || '',
              last_name: lawyer.last_name || data.data.lawyer?.name?.split(' ').slice(1).join(' ') || '',
              designation: lawyer.designation || data.data.lawyer?.designation || '',
              years_of_experience: lawyer.years_of_experience || null,
              bar_council_number: lawyer.bar_council_number || null,
              profile_photo: lawyer.profile_photo || data.data.lawyer?.photo || null,
              practice_areas: lawyer.practice_areas || null
            });
            setStep(2); // Skip lawyer selection for individual pages
          }
        } else {
          toast.error('Booking page not found');
        }
      } catch (error) {
        console.error('Error:', error);
        toast.error('Failed to load booking page');
      } finally {
        setLoading(false);
      }
    };
    fetchPageData();
  }, [slug]);

  // Fetch lawyer-specific appointment types when lawyer is selected
  useEffect(() => {
    if (!selectedLawyer) {
      setLawyerTypes([]);
      return;
    }
    const fetchLawyerTypes = async () => {
      try {
        const response = await fetch('/api/appointments/lawyer/' + selectedLawyer.id + '/types');
        const data = await response.json();
        if (data.success) {
          setLawyerTypes(data.data);
        }
      } catch (error) {
        console.error('Error fetching lawyer types:', error);
      }
    };
    fetchLawyerTypes();
  }, [selectedLawyer]);

  // Fetch available dates when lawyer and type selected
  useEffect(() => {
    if (!selectedLawyer || !selectedType) return;
    const fetchDates = async () => {
      try {
        const response = await fetch(
          '/api/appointments/available-dates?employee_id=' + selectedLawyer.id + 
          '&appointment_type_id=' + selectedType.id + '&days=30'
        );
        const data = await response.json();
        if (data.success) setAvailableDates(data.data);
      } catch (error) {
        console.error('Error fetching dates:', error);
      }
    };
    fetchDates();
  }, [selectedLawyer, selectedType, currentMonth]);

  // Fetch slots when date selected
  useEffect(() => {
    if (!selectedDate || !selectedLawyer || !selectedType) return;
    const fetchSlots = async () => {
      setSlotsLoading(true);
      try {
        const response = await fetch(
          '/api/appointments/slots?employee_id=' + selectedLawyer.id + 
          '&date=' + selectedDate + '&appointment_type_id=' + selectedType.id
        );
        const data = await response.json();
        if (data.success) setAvailableSlots(data.data.filter(s => s.available));
      } catch (error) {
        console.error('Error fetching slots:', error);
      } finally {
        setSlotsLoading(false);
      }
    };
    fetchSlots();
  }, [selectedDate, selectedLawyer, selectedType]);

  // Fetch price
  useEffect(() => {
    if (!selectedDate || !selectedType) return;
    const fetchPrice = async () => {
      try {
        const response = await fetch(
          '/api/appointments/calculate-price?appointment_type_id=' + selectedType.id + 
          '&appointment_date=' + selectedDate + '&start_time=' + (selectedSlot || '10:00') + (selectedLawyer ? '&employee_id=' + selectedLawyer.id : '')
        );
        const data = await response.json();
        if (data.success) setPriceInfo(data.data);
      } catch (error) {
        console.error('Error fetching price:', error);
      }
    };
    fetchPrice();
  }, [selectedDate, selectedSlot, selectedType]);

  // ═══ OTP FUNCTIONS ═══
  React.useEffect(() => {
    if (otpTimer > 0) {
      const t = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpTimer]);

  const handleSendOTP = async (channel) => {
    if (channel === 'whatsapp') {
      const phoneErr = validatePhone(formData.phone);
      if (phoneErr) { setFieldErrors(prev => ({ ...prev, phone: phoneErr })); toast.error(phoneErr); return; }
    } else {
      if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        toast.error('Please enter a valid email address first'); return;
      }
    }
    setVerifyChannel(channel);
    setOtpLoading(true);
    setOtpError('');
    try {
      const body = channel === 'whatsapp'
        ? { phone: formData.phone, channel: 'whatsapp', purpose: 'booking', name: formData.name }
        : { email: formData.email, channel: 'email', purpose: 'booking', name: formData.name };
      const res = await fetch('/api/appointments/otp/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setOtpSent(true);
        setOtpTimer(60);
        setOtpCode('');
        toast.success(channel === 'whatsapp' ? 'Code sent to your WhatsApp!' : 'Code sent to your email!');
      } else {
        setOtpError(data.message || 'Failed to send code');
        toast.error(data.message || 'Failed to send code');
      }
    } catch (e) {
      setOtpError('Network error. Please try again.');
    }
    setOtpLoading(false);
  };

  const handleVerifyOTP = async () => {
    if (!otpCode || otpCode.length < 6) { setOtpError('Please enter the 6-digit code'); return; }
    setOtpLoading(true);
    setOtpError('');
    try {
      const body = verifyChannel === 'whatsapp'
        ? { phone: formData.phone, otp: otpCode, channel: 'whatsapp', purpose: 'booking' }
        : { email: formData.email, otp: otpCode, channel: 'email', purpose: 'booking' };
      const res = await fetch('/api/appointments/otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await res.json();
      if (data.success) {
        setOtpVerified(true);
        setVerifiedContact(verifyChannel === 'whatsapp' ? formData.phone : formData.email);
        toast.success('Verified successfully!');
      } else {
        setOtpError(data.message || 'Verification failed');
      }
    } catch (e) { setOtpError('Network error. Please try again.'); }
    setOtpLoading(false);
  };

  // Reset OTP if contact changes
  React.useEffect(() => {
    if (verifiedContact) {
      const current = verifyChannel === 'whatsapp' ? formData.phone : formData.email;
      if (current !== verifiedContact) {
        setOtpSent(false); setOtpVerified(false); setOtpCode(''); setOtpError(''); setVerifiedContact(''); setVerifyChannel('');
      }
    }
  }, [formData.phone, formData.email, verifiedContact, verifyChannel]);

  const handleSubmit = async () => {
    const nameErr = validateName(formData.name);
    const phoneErr = validatePhone(formData.phone);
    const emailErr = validateEmail(formData.email);
    
    if (nameErr || phoneErr || emailErr) {
      setFieldErrors({ name: nameErr, phone: phoneErr, email: emailErr });
      if (phoneErr) {
        toast.error(phoneErr);
      } else if (nameErr) {
        toast.error(nameErr);
      } else if (emailErr) {
        toast.error(emailErr);
      }
      return;
    }
    setSubmitting(true);

    // Verify existing client if required
    if (selectedType?.is_for_existing_clients_only) {
      try {
        const verifyResponse = await fetch(
          '/api/appointments/verify-client?phone=' + encodeURIComponent(formData.phone) + 
          '&email=' + encodeURIComponent(formData.email || '')
        );
        const verifyData = await verifyResponse.json();
        if (!verifyData.data?.verified) {
          setShowVerificationModal(true);
          setSubmitting(false);
          return;
        }
      } catch (e) {
        console.error('Verification error:', e);
      }
    }

    try {
      // Check if this is a paid appointment type
      const isPaidAppointment = selectedType?.is_paid && parseFloat(selectedType?.fee_amount || priceInfo?.final_price || 0) > 0;
      
      if (isPaidAppointment) {
        // Create Razorpay payment order
        const orderResponse = await fetch('/api/appointments/payment/create-order', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            appointment_type_id: selectedType.id,
            employee_id: selectedLawyer.id,
            appointment_date: selectedDate,
            start_time: selectedSlot,
            client_name: formData.name,
            client_email: formData.email,
            client_phone: formData.phone,
            office_id: pageData.office_id || 1
          })
        });
        const orderData = await orderResponse.json();
        
        if (!orderData.success) {
          if (!orderData.payment_required) {
            // Payment not required (free appointment or payment not configured)
            await bookAppointmentWithoutPayment();
            return;
          }
          toast.error(orderData.message || 'Failed to create payment order');
          setSubmitting(false);
          return;
        }
        
        // Load Razorpay script if not loaded
        if (!window.Razorpay) {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          document.body.appendChild(script);
          await new Promise(resolve => script.onload = resolve);
        }
        
        // Open Razorpay payment modal
        setPaymentProcessing(true);
        const options = {
          key: orderData.key_id,
          amount: orderData.order.amount,
          currency: orderData.order.currency,
          name: pageData.company_name || 'Legal Consultation',
          description: orderData.appointment_type + ' - ' + selectedLawyer.first_name + ' ' + selectedLawyer.last_name,
          order_id: orderData.order.id,
          prefill: {
            name: formData.name,
            email: formData.email,
            contact: formData.phone
          },
          theme: { color: themeColor },
          handler: async function(response) {
            // Payment successful - verify and book
            try {
              const verifyResponse = await fetch('/api/appointments/payment/verify-and-book', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  appointment_type_id: selectedType.id,
                  employee_id: selectedLawyer.id,
                  appointment_date: selectedDate,
                  start_time: selectedSlot,
                  end_time: calculateEndTime(selectedSlot, selectedType.duration_minutes),
                  location_type: formData.location_type,
                  client_name: formData.name,
                  client_email: formData.email,
                  client_phone: formData.phone,
                  client_notes: formData.notes,
                  office_id: pageData.office_id || 1
                })
              });
              const verifyData = await verifyResponse.json();
              if (verifyData.success) {
                setBooking(verifyData.data);
                setStep(5);
                toast.success('Payment successful! Appointment confirmed.');
              } else {
                toast.error(verifyData.message || 'Payment verification failed');
              }
            } catch (err) {
              toast.error('Payment verification failed');
            } finally {
              setPaymentProcessing(false);
              setSubmitting(false);
            }
          },
          modal: {
            ondismiss: function() {
              setPaymentProcessing(false);
              setSubmitting(false);
              toast.error('Payment cancelled');
            }
          }
        };
        
        const razorpay = new window.Razorpay(options);
        razorpay.open();
        return; // Don't continue - handler will complete the flow
        
      } else {
        // Free appointment - book directly
        await bookAppointmentWithoutPayment();
      }
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to book appointment');
      setPaymentProcessing(false);
    } finally {
      if (!paymentProcessing) setSubmitting(false);
    }
  };
  
  // Helper function to calculate end time
  const calculateEndTime = (startTime, durationMinutes) => {
    const [hours, minutes] = startTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + (durationMinutes || 30);
    const endHours = Math.floor(totalMinutes / 60);
    const endMins = totalMinutes % 60;
    return String(endHours).padStart(2, '0') + ':' + String(endMins).padStart(2, '0');
  };
  
  // Book appointment without payment
  const bookAppointmentWithoutPayment = async () => {
    const response = await fetch('/api/appointments/book', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        booking_page_id: pageData.id,
        appointment_type_id: selectedType.id,
        employee_id: selectedLawyer.id,
        appointment_date: selectedDate,
        start_time: selectedSlot,
        guest_name: formData.name,
        guest_phone: formData.phone,
        guest_email: formData.email,
        location_type: formData.location_type,
        client_notes: formData.notes,
        intake_responses: formData.intake_responses
      })
    });
    const data = await response.json();
    if (data.success) {
      setBooking(data.data);
      setStep(5);
      toast.success('Appointment booked successfully!');
    } else {
      toast.error(data.message || 'Booking failed');
    }
    setSubmitting(false);
  };


  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [h, m] = timeStr.split(':');
    const hour = parseInt(h);
    return (hour > 12 ? hour - 12 : hour) + ':' + m + ' ' + (hour >= 12 ? 'PM' : 'AM');
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    // Add empty cells for days before the 1st
    for (let i = 0; i < firstDay.getDay(); i++) days.push(null);
    // Add each day of the month using local date format (YYYY-MM-DD)
    for (let i = 1; i <= lastDay.getDate(); i++) {
      const mm = String(month + 1).padStart(2, '0');
      const dd = String(i).padStart(2, '0');
      days.push(year + '-' + mm + '-' + dd);
    }
    return days;
  };

  const isDateAvailable = (dateStr) => availableDates.some(d => d.date === dateStr);

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800">Page Not Found</h1>
          <p className="text-gray-500 mt-2">The booking page you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            {pageData.logo_url && (
              <img src={pageData.logo_url} alt="Logo" className="h-12 object-contain" />
            )}
            <div>
              <h1 className="text-xl font-bold text-gray-800">{pageData.title}</h1>
              {pageData.subtitle && <p className="text-sm text-gray-500">{pageData.subtitle}</p>}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-sm text-gray-600">
            {pageData.display_phone && (
              <a href={'tel:' + pageData.display_phone} className="flex items-center gap-1 hover:text-blue-600">
                <Phone className="w-4 h-4" /> {pageData.display_phone}
              </a>
            )}
            {pageData.display_email && (
              <a href={'mailto:' + pageData.display_email} className="flex items-center gap-1 hover:text-blue-600">
                <Mail className="w-4 h-4" /> {pageData.display_email}
              </a>
            )}
            {pageData.website && (
              <a href={'https://' + pageData.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 hover:text-blue-600">
                <Globe className="w-4 h-4" /> {pageData.website}
              </a>
            )}
          </div>
        </div>
      </header>

      {/* Progress Steps */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center justify-center mb-8">
          {(pageData?.employee_id ? ['Service', 'Date & Time', 'Details', 'Confirmed'] : ['Lawyer', 'Service', 'Date & Time', 'Details', 'Confirmed']).map((label, index) => (
            <React.Fragment key={index}>
              {index > 0 && <div className={'h-1 w-8 md:w-16 ' + ((pageData?.employee_id ? step - 1 : step) > index ? 'bg-green-500' : 'bg-gray-200')} />}
              <div 
                className={'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ' +
                  ((() => { const s = pageData?.employee_id ? step - 1 : step; const i = index + 1; return s > i ? 'bg-green-500 text-white' : s === i ? 'text-white' : 'bg-gray-200 text-gray-500'; })())}
                style={(() => { const s = pageData?.employee_id ? step - 1 : step; return s === index + 1 ? { backgroundColor: themeColor } : {}; })()}
              >
                {(() => { const s = pageData?.employee_id ? step - 1 : step; return s > index + 1 ? '✓' : index + 1; })()}
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Step 1: Select Lawyer */}
        {step === 1 && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Select Your Lawyer</h2>
              <p className="text-sm text-gray-500 mt-1">Choose from our experienced team of legal professionals</p>
            </div>

            {/* Search Bar */}
            <div className="relative mb-4">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={lawyerSearch}
                onChange={(e) => setLawyerSearch(e.target.value)}
                placeholder="Search by name, designation, or practice area..."
                className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
              />
              {lawyerSearch && (
                <button onClick={() => setLawyerSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  ✕
                </button>
              )}
            </div>

            {/* Designation Filter Pills - hidden on mobile */}
            <div className="hidden md:flex flex-wrap gap-2 mb-5">
              {['all', ...new Set((pageData.lawyers || []).map(l => l.designation).filter(Boolean))].map((d) => (
                <button
                  key={d}
                  onClick={() => setDesignationFilter(d)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                    designationFilter === d
                      ? 'text-white shadow-sm'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  style={designationFilter === d ? { backgroundColor: themeColor } : {}}
                >
                  {d === 'all' ? 'All Lawyers' : d}
                </button>
              ))}
            </div>

            {/* Lawyer Cards */}
            {(() => {
              const filtered = (pageData.lawyers || []).filter(l => {
                const search = lawyerSearch.toLowerCase();
                const matchesSearch = !search ||
                  (l.first_name + ' ' + l.last_name).toLowerCase().includes(search) ||
                  (l.designation || '').toLowerCase().includes(search) ||
                  (l.practice_areas || '').toLowerCase().includes(search) ||
                  (l.specializations || '').toLowerCase().includes(search);
                const matchesDesignation = designationFilter === 'all' || l.designation === designationFilter;
                return matchesSearch && matchesDesignation;
              });

              if (filtered.length === 0) {
                return (
                  <div className="text-center py-12 text-gray-500">
                    <User className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="font-medium">No lawyers found</p>
                    <p className="text-sm mt-1">Try a different search term or filter</p>
                  </div>
                );
              }

              return (
                <div className="grid md:grid-cols-2 gap-3">
                  {filtered.map((lawyer) => {
                    const initials = (lawyer.first_name?.[0] || '') + (lawyer.last_name?.[0] || '');
                    const colors = ['#1e40af','#7c3aed','#059669','#dc2626','#d97706','#0891b2','#4f46e5','#be185d'];
                    const colorIdx = (lawyer.first_name?.charCodeAt(0) || 0) % colors.length;

                    return (
                      <div
                        key={lawyer.id}
                        onClick={() => { setSelectedLawyer(lawyer); setSelectedType(null); setStep(2); }}
                        className="group p-4 rounded-xl border border-gray-200 cursor-pointer transition-all hover:shadow-lg hover:border-blue-300 bg-white relative overflow-hidden"
                      >
                        <div className="absolute top-0 left-0 w-1 h-full transition-all group-hover:w-1.5" style={{ backgroundColor: colors[colorIdx] }} />
                        <div className="flex items-start gap-3 pl-2">
                          {lawyer.profile_photo ? (
                            <img src={lawyer.profile_photo} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-gray-100" />
                          ) : (
                            <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0" style={{ backgroundColor: colors[colorIdx] }}>
                              {initials}
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">
                              Adv. {lawyer.first_name} {lawyer.last_name}
                            </h3>
                            {lawyer.designation && (
                              <p className="text-sm text-gray-500 mt-0.5">{lawyer.designation}</p>
                            )}
                            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2">
                              {lawyer.years_of_experience && (
                                <span className="text-xs text-gray-400">{lawyer.years_of_experience}+ yrs exp</span>
                              )}
                              {lawyer.practice_areas && (
                                <span className="text-xs text-gray-400 truncate max-w-[200px]">{lawyer.practice_areas}</span>
                              )}
                            </div>
                            {lawyer.specializations && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {lawyer.specializations.split(',').slice(0, 3).map((s, i) => (
                                  <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">{s.trim()}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-500 transition-colors flex-shrink-0 mt-1" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Results count */}
            <p className="text-xs text-gray-400 text-center mt-4">
              {(pageData.lawyers || []).filter(l => {
                const s = lawyerSearch.toLowerCase();
                return (!s || (l.first_name+' '+l.last_name).toLowerCase().includes(s) || (l.designation||'').toLowerCase().includes(s) || (l.practice_areas||'').toLowerCase().includes(s) || (l.specializations||'').toLowerCase().includes(s)) && (designationFilter === 'all' || l.designation === designationFilter);
              }).length} of {(pageData.lawyers || []).length} lawyers
            </p>
          </div>
        )}

        {/* Step 2: Select Service Type (with lawyer-specific prices) */}
        {step === 2 && (
          <div>
            {/* Lawyer Profile Card for individual pages */}
            {pageData?.employee_id && selectedLawyer && (
              <div className="mb-6 p-5 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl border border-gray-200">
                <div className="flex items-center gap-4">
                  {selectedLawyer.profile_photo ? (
                    <img src={selectedLawyer.profile_photo.startsWith('http') ? selectedLawyer.profile_photo : '/uploads/' + selectedLawyer.profile_photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm" />
                  ) : (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-lg font-semibold shadow-sm" style={{backgroundColor: themeColor}}>
                      {(selectedLawyer.first_name?.[0] || '')}{(selectedLawyer.last_name?.[0] || '')}
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">Adv. {selectedLawyer.first_name} {selectedLawyer.last_name}</h3>
                    <p className="text-sm text-gray-600">{selectedLawyer.designation || 'Advocate'}</p>
                    <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-gray-500">
                      {selectedLawyer.years_of_experience && <span>{selectedLawyer.years_of_experience}+ years experience</span>}
                      {selectedLawyer.bar_council_number && <span>Bar: {selectedLawyer.bar_council_number}</span>}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center gap-2 mb-6">
              <button onClick={() => { if (!pageData.employee_id) { setStep(1); setSelectedLawyer(null); } }} className={"p-2 hover:bg-gray-100 rounded-lg" + (pageData.employee_id ? " invisible" : "")}>
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">Select Service Type</h2>
              {selectedLawyer && (
                <span className="text-sm text-gray-500 ml-2">
                  for Adv. {selectedLawyer.first_name} {selectedLawyer.last_name}
                </span>
              )}
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {(lawyerTypes.length > 0 ? lawyerTypes : pageData.appointment_types)?.map((type) => (
                <div
                  key={type.id}
                  onClick={() => { setSelectedType(type); setStep(3); }}
                  className="p-5 rounded-xl border-2 cursor-pointer transition-all hover:shadow-lg bg-white border-gray-200 hover:border-blue-300"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-800 text-lg">{type.name}</h3>
                      <p className="text-gray-500 text-sm mt-1">{type.description}</p>
                      <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                        <Clock className="w-3 h-3" /> {type.duration_minutes} mins
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      {parseFloat(type.fee_amount) > 0 ? (
                        <span className="text-lg font-bold" style={{ color: themeColor }}>
                          ₹{parseFloat(type.fee_amount).toLocaleString()}
                        </span>
                      ) : (
                        <span className="text-green-600 font-semibold">Free</span>
                      )}
                      {type.is_for_existing_clients_only === 1 && (
                        <div className="text-xs text-orange-500 mt-1 flex items-center gap-1 justify-end">
                          <Lock className="w-3 h-3" /> Existing Clients
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Step 3: Select Date & Time */}
        {step === 3 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <button onClick={() => setStep(2)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">Select Date & Time</h2>
            </div>

            {/* Calendar */}
            <div className="bg-white rounded-xl p-6 shadow-sm border mb-6">
              <div className="flex items-center justify-between mb-4">
                <button onClick={prevMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <h3 className="font-semibold">
                  {currentMonth.toLocaleDateString(getOfficeSettings().locale, { month: 'long', year: 'numeric' })}
                </h3>
                <button onClick={nextMonth} className="p-2 hover:bg-gray-100 rounded-lg">
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
                  <div key={d} className="py-2 font-medium text-gray-500">{d}</div>
                ))}
                {getDaysInMonth(currentMonth).map((date, i) => (
                  <button
                    key={i}
                    disabled={!date || !isDateAvailable(date)}
                    onClick={() => date && isDateAvailable(date) && setSelectedDate(date)}
                    className={'py-2 rounded-lg transition-all ' +
                      (!date ? '' :
                      selectedDate === date ? 'text-white font-semibold' :
                      isDateAvailable(date) ? 'hover:bg-blue-50 text-gray-800' : 'text-gray-300 cursor-not-allowed')
                    }
                    style={selectedDate === date ? { backgroundColor: themeColor } : {}}
                  >
                    {date ? parseInt(date.split('-')[2]) : ''}
                  </button>
                ))}
              </div>
            </div>

            {/* Time Slots */}
            {selectedDate && (
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                <h3 className="font-semibold mb-4">Available Times for {formatDate(selectedDate)}</h3>
                {slotsLoading ? (
                  <div className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
                ) : availableSlots.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No slots available for this date</p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {availableSlots.map((slot) => (
                      <button
                        key={slot.start}
                        onClick={() => setSelectedSlot(slot.start)}
                        className={'py-2 px-3 rounded-lg border text-sm transition-all ' +
                          (selectedSlot === slot.start ? 'text-white border-transparent' : 'border-gray-200 hover:border-blue-300')
                        }
                        style={selectedSlot === slot.start ? { backgroundColor: themeColor } : {}}
                      >
                        {formatTime(slot.start)}
                      </button>
                    ))}
                  </div>
                )}
                {selectedSlot && (
                  <div className="mt-6 text-center">
                    <button 
                      onClick={() => setStep(4)}
                      className="px-8 py-3 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all"
                      style={{ backgroundColor: themeColor }}
                    >
                      Continue
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Step 4: Enter Details */}
        {step === 4 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <button onClick={() => setStep(3)} className="p-2 hover:bg-gray-100 rounded-lg">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h2 className="text-xl font-semibold text-gray-800">Your Details</h2>
            </div>

            <div className="max-w-xl mx-auto">
              <div className="bg-white rounded-xl p-6 shadow-sm border">
                {/* Summary */}
                <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-800 mb-2">Appointment Summary</h4>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Service:</strong> {selectedType?.name}</p>
                    <p><strong>Lawyer:</strong> Adv. {selectedLawyer?.first_name} {selectedLawyer?.last_name}</p>
                    <p><strong>Date:</strong> {formatDate(selectedDate)}</p>
                    <p><strong>Time:</strong> {formatTime(selectedSlot)}</p>
                    {priceInfo && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2">
                          <p><strong>Fee:</strong> ₹{priceInfo.final_price?.toLocaleString()}</p>
                          {priceInfo.applied_rules?.length > 0 && (
                            <button 
                              type="button"
                              onClick={() => setShowPriceBreakdown(!showPriceBreakdown)}
                              className="text-xs text-blue-600 hover:text-blue-800 underline"
                            >
                              {showPriceBreakdown ? 'Hide breakdown' : 'View breakdown'}
                            </button>
                          )}
                        </div>
                        {showPriceBreakdown && priceInfo.applied_rules?.length > 0 && (
                          <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm border border-blue-100">
                            <div className="space-y-1">
                              <div className="flex justify-between text-gray-600">
                                <span>Base consultation fee:</span>
                                <span>₹{priceInfo.base_price?.toLocaleString()}</span>
                              </div>
                              {priceInfo.applied_rules.map((rule, idx) => (
                                <div key={idx} className="flex justify-between text-gray-600">
                                  <span>{rule.label} ({rule.multiplier}x):</span>
                                  <span className="text-orange-600">+₹{Math.round(priceInfo.base_price * (rule.multiplier - 1) * (idx === 0 ? 1 : priceInfo.applied_rules[idx-1]?.multiplier || 1)).toLocaleString()}</span>
                                </div>
                              ))}
                              <div className="border-t border-blue-200 pt-1 mt-1 flex justify-between font-semibold text-gray-800">
                                <span>Total:</span>
                                <span>₹{priceInfo.final_price?.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Form */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name *</label>
                    <input 
                      type="text" 
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Enter your full name" 
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^0-9+\-\s]/g, "");
                        setFormData({ ...formData, phone: value });
                        if (fieldErrors.phone) setFieldErrors(prev => ({ ...prev, phone: "" }));
                      }}
                      onBlur={(e) => handleFieldBlur("phone", e.target.value)}
                      disabled={otpVerified && verifyChannel === 'whatsapp'}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.phone ? "border-red-500 bg-red-50" : ""}`}
                      placeholder="Enter 10-digit mobile number"
                    />
                    {fieldErrors.phone && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {fieldErrors.phone}
                      </p>
                    )}
                    
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        if (fieldErrors.email) setFieldErrors(prev => ({ ...prev, email: '' }));
                      }}
                      onBlur={(e) => handleFieldBlur('email', e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${fieldErrors.email ? 'border-red-500 bg-red-50' : ''}`}
                      placeholder="Your email (optional)"
                    />
                    {fieldErrors.email && (
                      <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="w-4 h-4" />
                        {fieldErrors.email}
                      </p>
                    )}
                  </div>

                  {/* Location Type */}
                  
                  {/* ═══ VERIFICATION SECTION ═══ */}
                  {!otpVerified ? (
                    <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
                      <p className="text-sm font-semibold text-blue-800 mb-3">Verify your identity to proceed</p>
                      {!otpSent ? (
                        <div className="flex gap-2">
                          <button type="button" onClick={() => handleSendOTP('email')}
                            disabled={otpLoading || !formData.email}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-40 text-sm font-medium transition">
                            {otpLoading && verifyChannel === 'email' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                            Verify via Email
                          </button>
                          <button type="button" onClick={() => handleSendOTP('whatsapp')}
                            disabled={otpLoading || !formData.phone || formData.phone.replace(/\D/g, '').length < 10}
                            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 text-sm font-medium transition">
                            {otpLoading && verifyChannel === 'whatsapp' ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
                            Verify via WhatsApp
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p className="text-sm text-blue-700 mb-2">
                            {verifyChannel === 'email' ? '📧 Code sent to ' + formData.email : '💬 Code sent to WhatsApp on ' + formData.phone}
                          </p>
                          <div className="flex gap-2">
                            <input type="text" inputMode="numeric" maxLength={6} value={otpCode}
                              onChange={(e) => { setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6)); setOtpError(''); }}
                              className={`flex-1 px-4 py-2.5 border rounded-lg text-center text-lg font-mono tracking-widest focus:ring-2 focus:ring-blue-500 ${otpError ? 'border-red-500' : 'border-blue-300'}`}
                              placeholder="Enter 6-digit code" autoFocus />
                            <button type="button" onClick={handleVerifyOTP} disabled={otpLoading || otpCode.length < 6}
                              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium">
                              {otpLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Verify'}
                            </button>
                          </div>
                          {otpError && <p className="mt-1.5 text-sm text-red-600">{otpError}</p>}
                          <div className="flex items-center justify-between mt-2">
                            <button type="button" onClick={() => { setOtpSent(false); setVerifyChannel(''); setOtpCode(''); }}
                              className="text-xs text-blue-600 hover:underline">Try different method</button>
                            <button type="button" onClick={() => handleSendOTP(verifyChannel)}
                              disabled={otpTimer > 0 || otpLoading}
                              className="text-xs text-blue-600 hover:underline disabled:text-gray-400">
                              {otpTimer > 0 ? 'Resend in ' + otpTimer + 's' : 'Resend code'}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="text-sm font-medium text-green-700">
                        {verifyChannel === 'email' ? 'Email verified' : 'WhatsApp verified'}: {verifiedContact}
                      </span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Meeting Type</label>
                    <div className="hidden sm:grid sm:grid-cols-3 gap-2">
                      <button 
                        onClick={() => setFormData({ ...formData, location_type: 'in_person' })}
                        className={'p-3 rounded-lg border text-sm flex flex-col items-center gap-1 ' +
                          (formData.location_type === 'in_person' ? 'border-blue-500 bg-blue-50' : 'border-gray-200')}
                      >
                        <Building2 className="w-5 h-5" /> In Person
                      </button>
                      <button 
                        onClick={() => setFormData({ ...formData, location_type: 'video' })}
                        className={'p-3 rounded-lg border text-sm flex flex-col items-center gap-1 ' +
                          (formData.location_type === 'video' ? 'border-blue-500 bg-blue-50' : 'border-gray-200')}
                      >
                        <Video className="w-5 h-5" /> Video Call
                      </button>
                      <button 
                        onClick={() => setFormData({ ...formData, location_type: 'phone' })}
                        className={'p-3 rounded-lg border text-sm flex flex-col items-center gap-1 ' +
                          (formData.location_type === 'phone' ? 'border-blue-500 bg-blue-50' : 'border-gray-200')}
                      >
                        <PhoneCall className="w-5 h-5" /> Phone Call
                      </button>
                    </div>
                  </div>

                  {/* Intake Questions */}
                  {selectedType?.intake_questions?.length > 0 && (
                    <div className="space-y-4 pt-4 border-t">
                      <h4 className="font-medium text-gray-800">Additional Information</h4>
                      {selectedType.intake_questions.map((q) => (
                        <div key={q.id}>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            {q.question_text} {q.is_required ? '*' : ''}
                          </label>
                          {q.question_type === 'select' ? (
                            <select
                              value={formData.intake_responses[q.question_text] || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                intake_responses: { ...formData.intake_responses, [q.question_text]: e.target.value }
                              })}
                              className="w-full px-4 py-2 border rounded-lg"
                            >
                              <option value="">Select...</option>
                              {(typeof q.options === 'string' ? JSON.parse(q.options) : q.options || []).map((opt) => (
                                <option key={opt} value={opt}>{opt}</option>
                              ))}
                            </select>
                          ) : q.question_type === 'textarea' ? (
                            <textarea
                              value={formData.intake_responses[q.question_text] || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                intake_responses: { ...formData.intake_responses, [q.question_text]: e.target.value }
                              })}
                              className="w-full px-4 py-2 border rounded-lg"
                              rows={3}
                            />
                          ) : (
                            <input
                              type="text"
                              value={formData.intake_responses[q.question_text] || ''}
                              onChange={(e) => setFormData({
                                ...formData,
                                intake_responses: { ...formData.intake_responses, [q.question_text]: e.target.value }
                              })}
                              className="w-full px-4 py-2 border rounded-lg"
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      className="w-full px-4 py-2 border rounded-lg"
                      rows={3}
                      placeholder="Any specific details about your legal matter..."
                    />
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={submitting || !formData.name || !formData.phone || !otpVerified}
                  className="w-full mt-6 py-3 text-white rounded-lg font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: themeColor }}
                >
                  {submitting ? (
                    <><Loader2 className="w-5 h-5 animate-spin" /> Booking...</>
                  ) : (
                    <><CheckCircle className="w-5 h-5" /> Confirm Booking</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 5: Confirmation */}
        {step === 5 && booking && (
          <div className="text-center">
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg border overflow-hidden">
              {/* Success Header */}
              <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-8 py-6 text-white">
                <div className="w-16 h-16 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-white" />
                </div>
                <h2 className="text-2xl font-bold mb-1">Booking Confirmed!</h2>
                <p className="text-green-100">Your appointment has been scheduled successfully</p>
              </div>

              {/* Booking Reference */}
              <div className="bg-gray-50 px-8 py-4 border-b">
                <p className="text-sm text-gray-500">Booking Reference</p>
                <p className="text-2xl font-bold text-gray-800 tracking-wider">{booking.appointment_code}</p>
              </div>

              {/* Appointment Details */}
              <div className="px-8 py-6">
                <div className="grid md:grid-cols-2 gap-6 text-left">
                  {/* Left Column */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Lawyer</p>
                        <p className="font-semibold text-gray-800">{selectedLawyer?.first_name} {selectedLawyer?.last_name}</p>
                        <p className="text-sm text-gray-500">{selectedLawyer?.designation}</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Service</p>
                        <p className="font-semibold text-gray-800">{selectedType?.name}</p>
                        <p className="text-sm text-gray-500">{selectedType?.duration_minutes} minutes</p>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                        <Calendar className="w-5 h-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Date & Time</p>
                        <p className="font-semibold text-gray-800">{formatDate(booking.appointment_date)}</p>
                        <p className="text-sm text-gray-500">{formatTime(booking.start_time)} - {formatTime(booking.end_time || calculateEndTime(booking.start_time, selectedType?.duration_minutes))}</p>
                      </div>
                    </div>
                  </div>

                  {/* Right Column */}
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Location</p>
                        <p className="font-semibold text-gray-800">{formData.location_type === 'video' ? 'Video Call' : formData.location_type === 'phone' ? 'Phone Call' : 'In Person'}</p>
                        {formData.location_type === 'in_person' && (
                          <p className="text-sm text-gray-500">{pageData?.address || '262, Sector-38, Gurugram, Haryana, 122001'}</p>
                        )}
                        {formData.location_type === 'video' && (
                          <p className="text-sm text-gray-500">Link will be sent via email</p>
                        )}
                      </div>
                    </div>

                    {booking.fee > 0 && (
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <CreditCard className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wide">Payment</p>
                          <p className="font-semibold text-gray-800">₹{booking.fee}</p>
                          <p className="text-sm text-green-600 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3" /> Paid Successfully
                          </p>
                        </div>
                      </div>
                    )}

                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-pink-100 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-pink-600" />
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wide">Confirmation Sent To</p>
                        <p className="font-semibold text-gray-800">{formData.phone}</p>
                        <p className="text-sm text-gray-500">{formData.email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* What to Prepare */}
                <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-left">
                  <h4 className="font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" /> What to Prepare
                  </h4>
                  <ul className="text-sm text-amber-700 space-y-1">
                    <li>• Bring all relevant documents related to your case</li>
                    <li>• Carry a valid photo ID (Aadhaar/PAN/Passport)</li>
                    <li>• Arrive 10 minutes before your appointment time</li>
                    <li>• Note down any questions you want to discuss</li>
                  </ul>
                </div>

                {/* Cancellation Policy */}
                <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg text-left">
                  <h4 className="font-semibold text-gray-700 mb-1 text-sm">Cancellation Policy</h4>
                  <p className="text-xs text-gray-500">
                    Free cancellation up to 12 hours before the appointment. For cancellations within 12 hours, 
                    a fee may apply. Contact us at {pageData?.display_phone || '+91-9311111622'} for any changes.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="px-8 py-6 bg-gray-50 border-t">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <button
                    onClick={() => {
                      const event = {
                        text: `${selectedType?.name} with ${selectedLawyer?.first_name} ${selectedLawyer?.last_name}`,
                        dates: `${booking.appointment_date.replace(/-/g, '')}T${booking.start_time.replace(/:/g, '')}00/${booking.appointment_date.replace(/-/g, '')}T${(booking.end_time || calculateEndTime(booking.start_time, selectedType?.duration_minutes)).replace(/:/g, '')}00`,
                        location: formData.location_type === 'in_person' ? (pageData?.address || '262, Sector-38, Gurugram') : 'Online',
                        details: `Booking Ref: ${booking.appointment_code}`
                      };
                      window.open(`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.text)}&dates=${event.dates}&location=${encodeURIComponent(event.location)}&details=${encodeURIComponent(event.details)}`, '_blank');
                    }}
                    className="flex flex-col items-center justify-center gap-1 p-3 bg-white border rounded-lg hover:bg-gray-50 transition"
                  >
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <span className="text-xs font-medium text-gray-700">Add to Calendar</span>
                  </button>

                  <button
                    onClick={() => window.print()}
                    className="flex flex-col items-center justify-center gap-1 p-3 bg-white border rounded-lg hover:bg-gray-50 transition"
                  >
                    <Download className="w-5 h-5 text-green-600" />
                    <span className="text-xs font-medium text-gray-700">Print / Save</span>
                  </button>

                  
                  <a
                    href={`tel:${pageData?.display_phone || '+91-9311111622'}`}
                    className="flex flex-col items-center justify-center gap-1 p-3 bg-white border rounded-lg hover:bg-gray-50 transition"
                  >
                    <Phone className="w-5 h-5 text-purple-600" />
                    <span className="text-xs font-medium text-gray-700">Call Office</span>
                  </a>

                  <button
                    onClick={() => window.location.reload()}
                    className="flex flex-col items-center justify-center gap-1 p-3 text-white rounded-lg transition"
                    style={{ backgroundColor: themeColor }}
                  >
                    <Plus className="w-5 h-5" />
                    <span className="text-xs font-medium">Book Another</span>
                  </button>
                </div>

                {/* Share on WhatsApp */}
                <div className="mt-4">
                  
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(`✅ Appointment Confirmed!\n\n📋 Reference: ${booking.appointment_code}\n👨‍⚖️ Lawyer: ${selectedLawyer?.first_name} ${selectedLawyer?.last_name}\n📅 Date: ${formatDate(booking.appointment_date)}\n⏰ Time: ${formatTime(booking.start_time)}\n📍 Location: ${formData.location_type === 'in_person' ? (pageData?.address || 'Truzie Office') : formData.location_type === 'video' ? 'Video Call' : 'Phone Call'}\n\nTruzie International LLP\n${pageData?.display_phone || '+91-9311111622'}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 p-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition"
                  >
                    <MessageCircle className="w-5 h-5" />
                    <span className="font-medium">Share on WhatsApp</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}


      {/* Client Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                  <Lock className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">Client Verification Required</h3>
                  <p className="text-amber-100 text-sm">This service is for existing clients only</p>
                </div>
              </div>
            </div>
            <div className="px-6 py-5">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
                <p className="text-gray-700 text-sm leading-relaxed">
                  We couldn't verify your details in our client database. The appointment type
                  <strong className="text-amber-700"> "{selectedType?.name}"</strong> is reserved for clients who have previously engaged with our firm.
                </p>
              </div>
              <div className="space-y-3">
                <h4 className="font-medium text-gray-800">What you can do:</h4>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">1</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Verify your contact details</p>
                    <p className="text-xs text-gray-500">Ensure your phone/email matches our records</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">2</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Book a paid consultation</p>
                    <p className="text-xs text-gray-500">New clients can book <strong>Consultation (₹3,000)</strong></p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">3</span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-700 font-medium">Contact our office</p>
                    <p className="text-xs text-gray-500">Call <strong>{pageData?.display_phone || '+91-9311111622'}</strong></p>
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t flex gap-3">
              <button
                onClick={() => { setShowVerificationModal(false); setStep(1); }}
                className="flex-1 px-4 py-2.5 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium"
              >
                Choose Another Service
              </button>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="flex-1 px-4 py-2.5 text-white rounded-lg font-medium"
                style={{ backgroundColor: themeColor }}
              >
                Update My Details
              </button>
            </div>
          </div>
        </div>
      )}

        </div>
      {/* Footer */}
      <footer className="py-6 mt-8 border-t bg-white">
        <div className="max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© {new Date().getFullYear()} {pageData.title}. All rights reserved.</p>
          {pageData.display_address && (
            <p className="mt-1 flex items-center justify-center gap-1">
              <MapPin className="w-3 h-3" /> {pageData.display_address}
            </p>
          )}
        </div>
      </footer>

      <Toaster position="top-right" />
    </div>
  );
};

export default PublicBooking;
